import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../Configurations/Urls";
import {
  FALLBACK_INSTITUTE_TIMINGS,
  formatHourHeader,
  resolveTimelineSlots,
  timeToMinutes,
} from "./schedulingTimeline";
import "./SlotAvailability.css";

const formatMinutesToTime = (totalMinutes) => {
  const minutes = Math.max(0, Math.min(23 * 60 + 59, Number(totalMinutes) || 0));
  const hour24 = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const ampm = hour24 < 12 ? "AM" : "PM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${ampm}`;
};

const formatShiftRange = (startTime, endTime, fallbackName = "-") => {
  const start = timeToMinutes(startTime);
  const endRaw = timeToMinutes(endTime);
  if (!Number.isFinite(start) || !Number.isFinite(endRaw)) return fallbackName || "-";
  const end = endRaw <= start ? endRaw + 24 * 60 : endRaw;
  return `${formatMinutesToTime(start)} - ${formatMinutesToTime(end % (24 * 60))}`;
};

const formatBatchDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatBatchDateRange = (start, end) =>
  `${formatBatchDate(start)} - ${end ? formatBatchDate(end) : "Open"}`;

const normalizePersonName = (value) =>
  String(value ?? "")
    .replace(/[\n\r\t]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const isOngoingBatch = (batchWrapper) => {
  const status = String(
    batchWrapper?.batch?.status ?? batchWrapper?.status ?? ""
  )
    .trim()
    .toLowerCase();
  return status === "ongoing";
};

const hasOverlap = (startA, endA, startB, endB) =>
  Number.isFinite(startA) &&
  Number.isFinite(endA) &&
  Number.isFinite(startB) &&
  Number.isFinite(endB) &&
  startA < endB &&
  endA > startB;

const getCellState = (trainer, colStart, colEnd, booking) => {
  if (
    booking &&
    hasOverlap(booking.__startMinutes, booking.__endMinutes, colStart, colEnd)
  ) {
    return "booked";
  }
  if (hasOverlap(trainer.shiftStart, trainer.shiftEnd, colStart, colEnd)) {
    return "available";
  }
  return "unavailable";
};

const getBookingBarStyle = (booking, colStart, colspan) => {
  const spanMinutes = colspan * 60;
  const startOffset = Math.max(0, booking.__startMinutes - colStart);
  const duration = Math.max(0, booking.__endMinutes - booking.__startMinutes);
  const leftPct = (startOffset / spanMinutes) * 100;
  const widthPct = Math.min(100 - leftPct, (duration / spanMinutes) * 100);

  return {
    left: `${leftPct}%`,
    width: `${Math.max(widthPct, 18)}%`,
  };
};

const formatClassTime = (value) => {
  const minutes = timeToMinutes(value);
  return Number.isFinite(minutes) ? formatMinutesToTime(minutes) : String(value || "-");
};

const formatBatchLabel = (code, name) => {
  const batchCode = String(code || "").trim();
  const batchName = String(name || "").trim();
  if (!batchCode) return batchName || "-";
  if (!batchName || batchName === batchCode) return batchCode;
  return `${batchCode} — ${batchName}`;
};

const TrainerOccupiedLabelWithTooltip = ({ booking, colSpan = 1 }) => {
  const cellRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [tooltipCoords, setTooltipCoords] = useState(null);
  const batchCode = String(booking.__batchCode || "").trim() || "Occupied";
  const batchLabel = formatBatchLabel(booking.__batchCode, booking.__batchName);
  const floorLabel =
    booking.__floor && booking.__floor !== "-"
      ? ` (Floor ${booking.__floor})`
      : "";

  const updateTooltipPosition = () => {
    const el = cellRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const tooltipHeight = 220;
    const margin = 12;
    const isNarrow = window.innerWidth <= 760;
    const showBelow = rect.bottom + tooltipHeight + margin < window.innerHeight;
    setTooltipCoords({
      left: isNarrow
        ? margin
        : Math.min(
            Math.max(rect.left + rect.width / 2, 160),
            window.innerWidth - 160
          ),
      top: isNarrow
        ? showBelow
          ? rect.bottom + 10
          : Math.max(margin, window.innerHeight - tooltipHeight - margin)
        : showBelow
          ? Math.min(rect.bottom + 10, window.innerHeight - margin)
          : Math.max(rect.top - 10, margin),
      placement: isNarrow || showBelow ? "bottom" : "top",
      isNarrow,
    });
  };

  const showDetails = () => {
    updateTooltipPosition();
    setOpen(true);
  };

  const hideDetails = () => {
    setOpen(false);
    setTooltipCoords(null);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handleReposition = () => updateTooltipPosition();
    const handleOutside = (event) => {
      if (cellRef.current && !cellRef.current.contains(event.target)) {
        hideDetails();
      }
    };
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    document.addEventListener("pointerdown", handleOutside);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
      document.removeEventListener("pointerdown", handleOutside);
    };
  }, [open]);

  const tooltipContent =
    open && tooltipCoords
      ? createPortal(
          <div
            className={`trainer-sheet-batch-tooltip trainer-sheet-batch-tooltip--portal trainer-sheet-batch-tooltip--${tooltipCoords.placement}${tooltipCoords.isNarrow ? " trainer-sheet-batch-tooltip--mobile" : ""}`}
            style={{
              left: tooltipCoords.left,
              top: tooltipCoords.top,
              transform: tooltipCoords.isNarrow
                ? "none"
                : tooltipCoords.placement === "bottom"
                  ? "translateX(-50%)"
                  : "translate(-50%, -100%)",
            }}
            role="tooltip"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Batch</span>
              <span className="trainer-sheet-batch-tooltip__value">{batchLabel}</span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Dates</span>
              <span className="trainer-sheet-batch-tooltip__value">
                {formatBatchDateRange(booking.__batchStartDate, booking.__batchEndDate)}
              </span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Course</span>
              <span className="trainer-sheet-batch-tooltip__value">{booking.__course}</span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Class</span>
              <span className="trainer-sheet-batch-tooltip__value">
                {formatClassTime(booking.start_time)} - {formatClassTime(booking.end_time)}
              </span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Status</span>
              <span className="trainer-sheet-batch-tooltip__value">
                {booking.class_status || "-"}
              </span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Space</span>
              <span className="trainer-sheet-batch-tooltip__value">
                {booking.__spaceName}
                {floorLabel}
              </span>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <td
        ref={cellRef}
        colSpan={colSpan}
        className="trainer-sheet-cell trainer-sheet-cell--occupied-label"
        title="Tap or hover for details"
        onMouseEnter={showDetails}
        onMouseLeave={hideDetails}
        onClick={(e) => {
          e.stopPropagation();
          if (open) hideDetails();
          else showDetails();
        }}
      >
        <span className="slot-occupied-text">{batchCode}</span>
      </td>
      {tooltipContent}
    </>
  );
};

const BatchBookingCard = ({ booking, colStart, colspan }) => {
  const barRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [tooltipCoords, setTooltipCoords] = useState(null);
  const barStyle = getBookingBarStyle(booking, colStart, colspan);
  const batchLabel = formatBatchLabel(booking.__batchCode, booking.__batchName);
  const floorLabel =
    booking.__floor && booking.__floor !== "-"
      ? ` (Floor ${booking.__floor})`
      : "";

  const updateTooltipPosition = () => {
    const el = barRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const tooltipHeight = 210;
    const showBelow = rect.bottom + tooltipHeight + 16 < window.innerHeight;
    setTooltipCoords({
      left: rect.left + rect.width / 2,
      top: showBelow ? rect.bottom + 10 : rect.top - 10,
      placement: showBelow ? "bottom" : "top",
    });
  };

  useEffect(() => {
    if (!hovered) return undefined;
    const handleReposition = () => updateTooltipPosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [hovered]);

  const tooltipContent = hovered && tooltipCoords
    ? createPortal(
        <div
          className={`trainer-sheet-batch-tooltip trainer-sheet-batch-tooltip--portal trainer-sheet-batch-tooltip--${tooltipCoords.placement}`}
          style={{
            left: tooltipCoords.left,
            top: tooltipCoords.placement === "bottom" ? tooltipCoords.top : tooltipCoords.top,
            transform:
              tooltipCoords.placement === "bottom"
                ? "translateX(-50%)"
                : "translate(-50%, -100%)",
          }}
          role="tooltip"
        >
          <div className="trainer-sheet-batch-tooltip__row">
            <span className="trainer-sheet-batch-tooltip__label">Batch</span>
            <span className="trainer-sheet-batch-tooltip__value">{batchLabel}</span>
          </div>
          <div className="trainer-sheet-batch-tooltip__row">
            <span className="trainer-sheet-batch-tooltip__label">Dates</span>
            <span className="trainer-sheet-batch-tooltip__value">
              {formatBatchDateRange(booking.__batchStartDate, booking.__batchEndDate)}
            </span>
          </div>
          <div className="trainer-sheet-batch-tooltip__row">
            <span className="trainer-sheet-batch-tooltip__label">Course</span>
            <span className="trainer-sheet-batch-tooltip__value">{booking.__course}</span>
          </div>
          <div className="trainer-sheet-batch-tooltip__row">
            <span className="trainer-sheet-batch-tooltip__label">Class</span>
            <span className="trainer-sheet-batch-tooltip__value">
              {formatClassTime(booking.start_time)} - {formatClassTime(booking.end_time)}
            </span>
          </div>
          <div className="trainer-sheet-batch-tooltip__row">
            <span className="trainer-sheet-batch-tooltip__label">Status</span>
            <span className="trainer-sheet-batch-tooltip__value">
              {booking.class_status || "-"}
            </span>
          </div>
          <div className="trainer-sheet-batch-tooltip__row">
            <span className="trainer-sheet-batch-tooltip__label">Space</span>
            <span className="trainer-sheet-batch-tooltip__value">
              {booking.__spaceName}
              {floorLabel}
            </span>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <div className="trainer-sheet-booking-track">
        <div
          ref={barRef}
          className="trainer-sheet-batch-wrap"
          style={barStyle}
          onMouseEnter={() => {
            updateTooltipPosition();
            setHovered(true);
          }}
          onMouseLeave={() => {
            setHovered(false);
            setTooltipCoords(null);
          }}
        >
          <div className="trainer-sheet-batch">
            <strong title={booking.__batchCode}>{booking.__batchCode}</strong>
          </div>
        </div>
      </div>
      {tooltipContent}
    </>
  );
};

const buildTrainerRowCells = (trainer, slots) => {
  const cells = [];
  let index = 0;

  while (index < slots.length) {
    const slot = slots[index];
    const colStart = Number(slot.key.slice(0, 2)) * 60;
    const colEnd = colStart + 60;
    const prevColStart = colStart - 60;
    const booking =
      trainer.bookings.find((b) =>
        hasOverlap(b.__startMinutes, b.__endMinutes, colStart, colEnd)
      ) || null;
    const continuedFromPrev =
      booking &&
      index > 0 &&
      hasOverlap(booking.__startMinutes, booking.__endMinutes, prevColStart, colStart);

    if (booking && !continuedFromPrev) {
      let colspan = 1;
      let nextIndex = index + 1;
      while (nextIndex < slots.length) {
        const nextStart = Number(slots[nextIndex].key.slice(0, 2)) * 60;
        const nextEnd = nextStart + 60;
        if (hasOverlap(booking.__startMinutes, booking.__endMinutes, nextStart, nextEnd)) {
          colspan += 1;
          nextIndex += 1;
        } else {
          break;
        }
      }
      cells.push({
        key: `${trainer.id}-${slot.key}`,
        slot,
        colStart,
        colspan,
        booking,
        isBookingStart: true,
      });
      index += colspan;
    } else {
      cells.push({
        key: `${trainer.id}-${slot.key}`,
        slot,
        colStart,
        colspan: 1,
        booking: continuedFromPrev ? booking : null,
        isBookingStart: false,
      });
      index += 1;
    }
  }

  return cells;
};

const TrainerSlotAvailability = () => {
  const { userData } = useAuthStore();
  const token = localStorage.getItem("token");
  const organizationId =
    userData?.organization?.organization_id ||
    userData?.activeOrganizationEntity?.organization_id ||
    userData?.organization_id;

  const [selectedDate, setSelectedDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [instituteTimings, setInstituteTimings] = useState(FALLBACK_INSTITUTE_TIMINGS);
  const [apiTimelineSlots, setApiTimelineSlots] = useState([]);
  const [isMobileView, setIsMobileView] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth <= 760
  );

  useEffect(() => {
    const update = () => setIsMobileView(window.innerWidth <= 760);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const timelineSlots = useMemo(
    () =>
      resolveTimelineSlots({
        timelineSlots: apiTimelineSlots,
        instituteTimings,
      }),
    [apiTimelineSlots, instituteTimings]
  );

  const fetchData = async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const payload = {
        organization_id: organizationId,
        date: selectedDate,
        ...(appliedSearch.trim() ? { search: appliedSearch.trim() } : {}),
      };

      const response = await axios.post(`${MAIN_URL}/api/ems/trainer-slots`, payload, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      setRows(Array.isArray(response?.data?.data) ? response.data.data : []);
      setInstituteTimings(response?.data?.instituteTimings || FALLBACK_INSTITUTE_TIMINGS);
      setApiTimelineSlots(
        Array.isArray(response?.data?.timelineSlots) ? response.data.timelineSlots : []
      );
    } catch (error) {
      console.error("Failed to fetch trainer slots", error);
      setRows([]);
      setInstituteTimings(FALLBACK_INSTITUTE_TIMINGS);
      setApiTimelineSlots([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, selectedDate, appliedSearch]);

  const handleSearch = () => {
    setAppliedSearch(search.trim());
  };

  const trainersForGrid = useMemo(() => {
    const mapped = (Array.isArray(rows) ? rows : []).map((trainer) => {
      const shiftStart = timeToMinutes(trainer?.workshift?.work_shift_start_time);
      const shiftEndRaw = timeToMinutes(trainer?.workshift?.work_shift_end_time);
      const shiftEnd =
        Number.isFinite(shiftStart) && Number.isFinite(shiftEndRaw) && shiftEndRaw <= shiftStart
          ? shiftEndRaw + 24 * 60
          : shiftEndRaw;

      const bookings = (Array.isArray(trainer?.batches) ? trainer.batches : [])
        .filter((batch) => isOngoingBatch(batch))
        .flatMap((batch) =>
          (Array.isArray(batch?.slots) ? batch.slots : []).map((slot) => {
            const start = timeToMinutes(slot?.start_time);
            const endRaw = timeToMinutes(slot?.end_time);
            const end =
              Number.isFinite(start) && Number.isFinite(endRaw) && endRaw <= start
                ? endRaw + 24 * 60
                : endRaw;
            return {
              ...slot,
              __startMinutes: start,
              __endMinutes: end,
              __batchId: batch?.batch_id || batch?.batch?.organization_ems_batch_id || null,
              __batchCode: batch?.batch?.batch_code || batch?.batch_code || "-",
              __batchName: batch?.batch?.batch_name || batch?.batch_name || "-",
              __batchStartDate: batch?.batch?.start_date || null,
              __batchEndDate: batch?.batch?.end_date || null,
              __course:
                batch?.batch?.training_program?.training_program_name ||
                batch?.training_program ||
                "-",
              __spaceName: slot?.training_space?.space_name || "-",
              __floor:
                slot?.training_space?.floor_number === null ||
                slot?.training_space?.floor_number === undefined
                  ? "-"
                  : String(slot.training_space.floor_number),
            };
          })
        )
        .filter((b) => Number.isFinite(b.__startMinutes) && Number.isFinite(b.__endMinutes))
        .filter((booking, index, arr) => {
          const bookingKey = [
            booking?.class_date || "",
            booking?.__batchId || booking?.__batchCode || "",
            booking?.start_time || "",
            booking?.end_time || "",
          ].join("|");
          return (
            arr.findIndex((item) => {
              const itemKey = [
                item?.class_date || "",
                item?.__batchId || item?.__batchCode || "",
                item?.start_time || "",
                item?.end_time || "",
              ].join("|");
              return itemKey === bookingKey;
            }) === index
          );
        })
        .sort((a, b) => a.__startMinutes - b.__startMinutes);

      const orgUserId = trainer?.organization_user_id;
      const appUserId = trainer?.application_user_id;
      const name = normalizePersonName(
        trainer?.person?.full_name || trainer?.person?.name || "-"
      );

      return {
        id: String(
          (appUserId && Number(appUserId) > 0 ? `app-${appUserId}` : "") ||
            (orgUserId != null ? `user-${orgUserId}` : "") ||
            (trainer?.organization_person_id
              ? `person-${trainer.organization_person_id}`
              : "") ||
            `name-${name.toLowerCase()}`
        ),
        applicationUserId:
          appUserId != null && Number(appUserId) > 0 ? Number(appUserId) : null,
        organizationUserId:
          orgUserId != null && Number(orgUserId) > 0 ? Number(orgUserId) : null,
        name,
        shiftLabel:
          Number.isFinite(shiftStart) && Number.isFinite(shiftEnd)
            ? formatShiftRange(
                trainer?.workshift?.work_shift_start_time,
                trainer?.workshift?.work_shift_end_time
              )
            : "No shift assigned",
        shiftStart,
        shiftEnd,
        bookings,
      };
    });

    // One row per trainer: Shared Resource may return multiple org-user ids for the same person.
    const mergedByKey = new Map();
    mapped.forEach((trainer) => {
      const mergeKey =
        trainer.applicationUserId != null
          ? `app:${trainer.applicationUserId}`
          : trainer.name && trainer.name !== "-"
            ? `name:${trainer.name.toLowerCase()}`
            : `id:${trainer.id}`;

      if (!mergedByKey.has(mergeKey)) {
        mergedByKey.set(mergeKey, {
          ...trainer,
          bookings: [...trainer.bookings],
        });
        return;
      }

      const existing = mergedByKey.get(mergeKey);
      const combined = [...existing.bookings, ...trainer.bookings]
        .filter((booking, index, arr) => {
          const bookingKey = [
            booking?.class_date || "",
            booking?.__batchId || booking?.__batchCode || "",
            booking?.start_time || "",
            booking?.end_time || "",
          ].join("|");
          return (
            arr.findIndex((item) => {
              const itemKey = [
                item?.class_date || "",
                item?.__batchId || item?.__batchCode || "",
                item?.start_time || "",
                item?.end_time || "",
              ].join("|");
              return itemKey === bookingKey;
            }) === index
          );
        })
        .sort((a, b) => a.__startMinutes - b.__startMinutes);

      existing.bookings = combined;
      if (
        existing.shiftLabel === "No shift assigned" &&
        trainer.shiftLabel !== "No shift assigned"
      ) {
        existing.shiftLabel = trainer.shiftLabel;
        existing.shiftStart = trainer.shiftStart;
        existing.shiftEnd = trainer.shiftEnd;
      }
    });

    return Array.from(mergedByKey.values())
      .filter((trainer) => trainer.bookings.length > 0)
      .sort((a, b) =>
        String(a.name).localeCompare(String(b.name), undefined, {
          sensitivity: "base",
        })
      );
  }, [rows]);

  const trainerRows = useMemo(
    () =>
      trainersForGrid.map((trainer) => ({
        trainer,
        cells: buildTrainerRowCells(trainer, timelineSlots),
      })),
    [trainersForGrid, timelineSlots]
  );

  const isEmpty = useMemo(() => trainersForGrid.length === 0, [trainersForGrid]);

  return (
    <section className="slot-availability trainer-slot-sheet">
      <header className="slot-availability__header">
        <h2 className="slot-availability__title">Trainer Slot Availability</h2>
        <div className="slot-availability__legend trainer-sheet-legend">
          <span className="slot-availability__legend-item">
            <i className="trainer-sheet-legend-swatch trainer-sheet-legend-swatch--available" />
            Available (In Shift)
          </span>
          <span className="slot-availability__legend-item">
            <i className="trainer-sheet-legend-swatch trainer-sheet-legend-swatch--unavailable" />
            Unavailable (Off Shift)
          </span>
          <span className="slot-availability__legend-item">
            <i className="trainer-sheet-legend-swatch trainer-sheet-legend-swatch--occupied" />
            Occupied (Batch)
          </span>
        </div>
      </header>

      <div className="slot-availability__filters trainer-sheet-filters">
        <label className="slot-select">
          <span>Date</span>
          <input
            type="date"
            className="slot-select__input"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </label>
        <label className="slot-select">
          <span>Trainer</span>
          <input
            type="text"
            className="slot-select__input"
            placeholder="Search trainer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
        </label>
        <button type="button" onClick={handleSearch} className="search-button trainer-sheet-search-btn">
          {loading ? "Loading..." : "Search"}
        </button>
      </div>

      <div className="slot-availability__table-wrap trainer-sheet-wrap">
        <table className="trainer-sheet-table">
          <colgroup>
            <col className="trainer-sheet-col-trainer" />
            {timelineSlots.map((slot) => (
              <col key={slot.key} className="trainer-sheet-col-time" />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="trainer-sheet-corner">Trainer</th>
              {timelineSlots.map((slot) => (
                <th key={slot.key} className="trainer-sheet-time-head">
                  {formatHourHeader(slot.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isEmpty ? (
              <tr>
                <td
                  colSpan={timelineSlots.length + 1}
                  className="trainer-sheet-empty"
                >
                  No trainer slot data found.
                </td>
              </tr>
            ) : (
              trainerRows.map(({ trainer, cells }) => (
                <tr key={trainer.id}>
                  <th scope="row" className="trainer-sheet-trainer-cell">
                    <div className="trainer-sheet-trainer-name" title={trainer.name}>
                      {trainer.name}
                    </div>
                    <div className="trainer-sheet-trainer-shift" title={trainer.shiftLabel}>
                      {trainer.shiftLabel}
                    </div>
                  </th>
                  {cells.map((cell) => {
                    if (cell.isBookingStart && cell.booking) {
                      if (isMobileView) {
                        return (
                          <TrainerOccupiedLabelWithTooltip
                            key={cell.key}
                            booking={cell.booking}
                            colSpan={cell.colspan}
                          />
                        );
                      }
                      return (
                        <td
                          key={cell.key}
                          colSpan={cell.colspan}
                          className="trainer-sheet-cell trainer-sheet-cell--booked-span"
                        >
                          <BatchBookingCard
                            booking={cell.booking}
                            colStart={cell.colStart}
                            colspan={cell.colspan}
                          />
                        </td>
                      );
                    }

                    const colEnd = cell.colStart + 60;
                    const state = getCellState(trainer, cell.colStart, colEnd, cell.booking);

                    return (
                      <td
                        key={cell.key}
                        className={`trainer-sheet-cell trainer-sheet-cell--${state}`}
                      />
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="slot-availability__scroll-hint">Swipe sideways to see more hours →</p>
    </section>
  );
};

export default TrainerSlotAvailability;

